<script lang="ts" context="module">
  export const button = cva(
    [
      `
      relative
      
      inline-flex
      items-center
      justify-center
      gap-1.5

      font-medium

      whitespace-nowrap

      outline-none
      border
      border-transparent
      rounded-xs

      cursor-pointer

      transition
      motion-reduce:transition-none

      shadow-none
      focus-visible:ring

      disabled:opacity-40
      disabled:cursor-not-allowed
      `
    ],
    {
      variants: {
        variant: {
          primary: `
            bg-primary
            disabled:hover:bg-primary
            text-highlight
            hover:bg-primary/96
            active:bg-primary
          `,
          secondary: `
            bg-primary/18
            disabled:hover:bg-primary/18
            text-highlight
            border-0
            hover:bg-primary/14
            active:bg-primary/18
            focus-visible:border-accent
          `,
          ghost: `
            border-primary/14
            text-primary
            disabled:hover:bg-transparent
            hover:bg-primary/4
            active:bg-transparent
          `,
          accent: `
            bg-accent
            disabled:hover:bg-accent
            text-highlight
            hover:bg-accent/96
            active:bg-accent
          `,
          radio: `
            border-primary/80
            text-primary
            disabled:hover:bg-transparent
            hover:bg-primary/4
            active:bg-transparent
          `,
          blur: `
            bg-primary/40
            disabled:hover:bg-primary/40
            text-highlight
            border-0
            hover:bg-primary/32
            active:bg-primary/40
            focus-visible:border-accent
            backdrop-blur
          `
        },
        iconOnly: {
          true: '',
          false: ''
        },
        size: {
          xxs: `
            gap-1
            px-1.5 
            h-6
            text-xs/none   
          `,
          xs: `
            gap-1
            px-2 
            h-8
            text-sm/none   
          `,
          sm: `
            px-4 
            h-10
            text-sm
          `,
          md: `
            px-4.5 
            h-11
            text-md/none
          `
        },
        active: {
          true: ''
        },
        icon: {
          true: ''
        }
      },
      compoundVariants: [
        {
          iconOnly: true,
          size: 'sm',
          class: 'w-10 px-0'
        },
        {
          iconOnly: true,
          size: 'md',
          class: 'w-11 px-0'
        },
        {
          variant: 'radio',
          active: true,
          class: 'hover:bg-accent/4 border-accent text-accent-contrast'
        },
        { icon: true, size: 'xs', class: '!pl-1.5 !pr-2' },
        { icon: true, size: 'xxs', class: '!pl-1.5 !pr-2' }
      ]
    }
  );

  const iconSize: Record<
    NonNullable<VariantProps<typeof button>['size']>,
    ComponentProps<Icon>['size']
  > = {
    xxs: 'sm',
    xs: 'sm',
    sm: 'md',
    md: 'md'
  };
</script>

<script lang="ts">
  import { cva, type VariantProps } from 'class-variance-authority';

  import Icon from './icon/icon.svelte';

  import { cn } from '$lib/utils';

  import type { IconOptions } from './icon/icon.svelte';
  import Spinner from './spinner.svelte';
  import type { ComponentProps } from 'svelte';

  let className: string | undefined = undefined;
  export { className as class };

  export let as: 'button' | 'a' = 'button';
  export let disabled = false;
  export let variant: VariantProps<typeof button>['variant'] = 'primary';
  export let size: NonNullable<VariantProps<typeof button>['size']> = 'md';
  export let icon: IconOptions | undefined = undefined;
  export let loading: boolean | undefined = undefined;
  export let active: boolean | undefined = undefined;
  export let name: string | undefined = undefined;
</script>

<!-- eslint-disable-next-line svelte/valid-compile -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<svelte:element
  this={as}
  on:click
  on:mouseenter
  on:mouseleave
  on:keydown
  on:keyup
  on:keypress
  on:focus
  on:blur
  {disabled}
  aria-label={name}
  class={cn(
    button({ size, variant, active, iconOnly: !!icon && !$$slots.default, icon: !!icon }),
    !!icon && $$slots.default && 'pl-3.5',
    loading && 'pointer-events-none [&>*:not(.spinner)]:invisible',
    className
  )}
  {...$$restProps}
>
  {#if icon}
    <Icon {icon} size={iconSize[size]} />
  {/if}
  {#if $$slots.default}
    <span>
      <slot />
    </span>
  {/if}
  {#if loading}
    <Spinner class="spinner absolute" {size} />
  {/if}
</svelte:element>
